package br.com.heyagenda.data.repository

import br.com.heyagenda.data.local.UserLocalDataSource
import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreProfessionalDataSource
import br.com.heyagenda.data.remote.ProfessionalProfileDto
import br.com.heyagenda.domain.model.User
import br.com.heyagenda.domain.repository.AuthRepository
import dev.gitlive.firebase.auth.FirebaseUser
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AuthRepositoryImpl(
    private val authDataSource: FirebaseAuthDataSource,
    private val firestoreDataSource: FirestoreProfessionalDataSource,
    private val localDataSource: UserLocalDataSource
) : AuthRepository {

    override val currentUser: StateFlow<User?> = _currentUser.asStateFlow()

    init {
        // 1. Restore local cache immediately
        val cached = localDataSource.getUser()
        if (cached != null) {
            _currentUser.value = cached
        }

        // 2. Observe & sync with Firebase Auth / Firestore in background
        CoroutineScope(Dispatchers.Default).launch {
            try {
                val firebaseUser = authDataSource.currentFirebaseUser
                if (firebaseUser != null) {
                    val updated = fetchUserProfile(firebaseUser)
                    localDataSource.saveUser(updated)
                    _currentUser.value = updated
                }

                authDataSource.authStateChanged.collect { user ->
                    if (user != null) {
                        val updated = fetchUserProfile(user)
                        localDataSource.saveUser(updated)
                        _currentUser.value = updated
                    } else {
                        localDataSource.clearUser()
                        _currentUser.value = null
                    }
                }
            } catch (e: Throwable) {
                // Background auth collection error handling
            }
        }
    }

    override suspend fun login(email: String, pass: String): Result<Unit> {
        return try {
            val authResult = authDataSource.signIn(email.trim(), pass)
            val user = authResult.user
            if (user != null) {
                val profile = fetchUserProfile(user)
                localDataSource.saveUser(profile)
                _currentUser.value = profile
                Result.success(Unit)
            } else {
                Result.failure(Exception("Usuário não encontrado."))
            }
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun loginWithGoogle(): Result<Unit> {
        return try {
            val platformResult = br.com.heyagenda.domain.auth.PlatformAuthManager.signInWithGoogle()
            val authData = platformResult.getOrThrow()

            val firebaseUser = if (authData.idToken != null) {
                val authResult = authDataSource.signInWithGoogle(idToken = authData.idToken, accessToken = authData.accessToken)
                authResult.user
            } else {
                authDataSource.reloadUser() ?: authDataSource.currentFirebaseUser
            }

            if (firebaseUser != null) {
                if (firebaseUser.displayName.isNullOrBlank() && !authData.displayName.isNullOrBlank()) {
                    authDataSource.updateDisplayName(authData.displayName)
                }

                val profile = fetchUserProfile(firebaseUser)
                localDataSource.saveUser(profile)
                _currentUser.value = profile
                Result.success(Unit)
            } else {
                Result.failure(Exception("Falha ao autenticar com Google no Firebase."))
            }
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun loginWithApple(): Result<Unit> {
        return try {
            val platformResult = br.com.heyagenda.domain.auth.PlatformAuthManager.signInWithApple()
            val authData = platformResult.getOrThrow()

            val firebaseUser = if (authData.idToken != null) {
                val authResult = authDataSource.signInWithApple(
                    idToken = authData.idToken,
                    rawNonce = authData.rawNonce,
                    accessToken = authData.accessToken
                )
                authResult.user
            } else {
                authDataSource.reloadUser() ?: authDataSource.currentFirebaseUser
            }

            if (firebaseUser != null) {
                if (firebaseUser.displayName.isNullOrBlank() && !authData.displayName.isNullOrBlank()) {
                    authDataSource.updateDisplayName(authData.displayName)
                }

                val profile = fetchUserProfile(firebaseUser)
                localDataSource.saveUser(profile)
                _currentUser.value = profile
                Result.success(Unit)
            } else {
                Result.failure(Exception("Falha ao autenticar com Apple no Firebase."))
            }
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun signUp(
        name: String,
        studioName: String,
        specialty: String,
        email: String,
        pass: String
    ): Result<Unit> {
        return try {
            val authResult = authDataSource.createUser(email.trim(), pass)
            val user = authResult.user
            if (user != null) {
                val cleanName = name.trim()
                val cleanStudio = studioName.trim()
                val slug = firestoreDataSource.generateUniqueSlug(cleanStudio.ifBlank { cleanName }, user.uid)

                val appUser = User(
                    uid = user.uid,
                    name = cleanName,
                    email = email.trim(),
                    studioName = cleanStudio,
                    specialty = specialty,
                    slug = slug
                )

                // Update Firebase Auth Display Name
                authDataSource.updateDisplayName(cleanName)

                // Save locally for instant access
                localDataSource.saveUser(appUser)
                _currentUser.value = appUser

                // Save to Firestore professionals/{uid}
                try {
                    firestoreDataSource.saveProfile(
                        uid = user.uid,
                        profile = ProfessionalProfileDto(
                            name = cleanName,
                            studioName = cleanStudio,
                            specialty = specialty,
                            email = email.trim(),
                            slug = slug
                        )
                    )
                } catch (e: Throwable) {
                    println(">>> [AuthRepo] saveProfile non-fatal error during signUp: ${e.message}")
                }

                Result.success(Unit)
            } else {
                Result.failure(Exception("Falha ao criar usuário."))
            }
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun completeSocialSignUp(
        name: String,
        studioName: String,
        specialty: String
    ): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado."))
        val cleanName = name.trim()
        val cleanStudio = studioName.trim()
        val cleanSpecialty = specialty.trim()

        if (cleanName.isBlank()) {
            return Result.failure(IllegalArgumentException("Informe o seu nome completo."))
        }
        if (cleanStudio.isBlank()) {
            return Result.failure(IllegalArgumentException("Informe o nome do seu estúdio ou cadeira."))
        }

        return try {
            val existing = firestoreDataSource.fetchProfile(user.uid) ?: ProfessionalProfileDto()
            val finalEmail = if (existing.email.isNotBlank()) existing.email else user.email
            val slug = firestoreDataSource.generateUniqueSlug(cleanStudio, user.uid)

            val updatedDto = existing.copy(
                name = cleanName,
                studioName = cleanStudio,
                specialty = cleanSpecialty,
                email = finalEmail,
                slug = slug
            )
            firestoreDataSource.saveProfile(user.uid, updatedDto)

            try {
                authDataSource.updateDisplayName(cleanName)
            } catch (_: Throwable) {}

            val updatedUser = user.copy(
                name = cleanName,
                studioName = cleanStudio,
                specialty = cleanSpecialty,
                email = finalEmail,
                slug = slug
            )
            localDataSource.saveUser(updatedUser)
            _currentUser.value = updatedUser

            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun updateSlug(newSlug: String): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        val result = firestoreDataSource.updateSlug(user.uid, newSlug)
        if (result.isSuccess) {
            val clean = br.com.heyagenda.domain.util.SlugUtils.toSlug(newSlug)
            val updated = user.copy(slug = clean)
            _currentUser.value = updated
            localDataSource.saveUser(updated)
        }
        return result
    }

    override suspend fun checkSlugAvailable(slug: String): Boolean {
        val user = _currentUser.value
        return firestoreDataSource.isSlugAvailable(slug, user?.uid)
    }

    override suspend fun updateWebSettings(
        themeAccentColor: String?,
        instagramHandle: String?,
        address: String?,
        announcementBanner: String?
    ): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        val result = firestoreDataSource.updateWebSettings(
            uid = user.uid,
            themeAccentColor = themeAccentColor,
            instagramHandle = instagramHandle,
            address = address,
            announcementBanner = announcementBanner
        )
        if (result.isSuccess) {
            val updated = user.copy(
                themeAccentColor = themeAccentColor,
                instagramHandle = instagramHandle,
                address = address,
                announcementBanner = announcementBanner
            )
            _currentUser.value = updated
            localDataSource.saveUser(updated)
        }
        return result
    }

    override suspend fun fetchVisits(): Pair<Long, Long> {
        val user = _currentUser.value ?: return Pair(0L, 0L)
        return firestoreDataSource.fetchVisits(user.uid)
    }

    override suspend fun updateProfile(
        name: String,
        studioName: String,
        specialty: String,
        phone: String?,
        instagramHandle: String?,
        address: String?
    ): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        val cleanName = name.trim()
        val cleanStudio = studioName.trim()
        val cleanSpecialty = specialty.trim()
        val cleanPhone = phone?.trim()?.ifBlank { null }
        val cleanInstagram = instagramHandle?.trim()?.removePrefix("@")?.ifBlank { null }
        val cleanAddress = address?.trim()?.ifBlank { null }

        return try {
            val existing = firestoreDataSource.fetchProfile(user.uid) ?: ProfessionalProfileDto()
            val updatedDto = existing.copy(
                name = cleanName,
                studioName = cleanStudio,
                specialty = cleanSpecialty,
                phone = cleanPhone ?: existing.phone,
                instagramHandle = cleanInstagram ?: existing.instagramHandle,
                address = cleanAddress ?: existing.address
            )
            firestoreDataSource.saveProfile(user.uid, updatedDto)

            authDataSource.updateDisplayName(cleanName)

            val updatedUser = user.copy(
                name = cleanName.ifBlank { "Profissional" },
                studioName = cleanStudio.ifBlank { null },
                specialty = cleanSpecialty.ifBlank { null },
                phone = cleanPhone ?: user.phone,
                instagramHandle = cleanInstagram ?: user.instagramHandle,
                address = cleanAddress ?: user.address
            )
            _currentUser.value = updatedUser
            localDataSource.saveUser(updatedUser)

            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun updateSubscriptionTier(tier: String, billingCycle: String, expiresAt: Long?): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        val updatedUser = user.copy(
            subscriptionTier = tier,
            subscriptionBillingCycle = billingCycle,
            subscriptionExpiresAt = expiresAt ?: user.subscriptionExpiresAt
        )
        _currentUser.value = updatedUser
        localDataSource.saveUser(updatedUser)
        return firestoreDataSource.updateSubscriptionTier(user.uid, tier, billingCycle, expiresAt)
    }

    override suspend fun sendPasswordResetEmail(): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        if (user.email.isBlank()) {
            return Result.failure(Exception("E-mail não cadastrado."))
        }
        return try {
            authDataSource.sendPasswordResetEmail(user.email)
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    override suspend fun logout() {
        try {
            authDataSource.signOut()
            localDataSource.clearUser()
            _currentUser.value = null
        } catch (e: Throwable) {
            localDataSource.clearUser()
            _currentUser.value = null
        }
    }

    override fun getAuthProvider(): String = authDataSource.getAuthProvider()

    override suspend fun deleteAccount(password: String?): Result<Unit> {
        val user = _currentUser.value ?: return Result.failure(Exception("Usuário não autenticado"))
        val existingProfile = firestoreDataSource.fetchProfile(user.uid) ?: ProfessionalProfileDto(
            name = user.name,
            studioName = user.studioName ?: "",
            specialty = user.specialty ?: "",
            email = user.email,
            slug = user.slug,
            themeAccentColor = user.themeAccentColor,
            instagramHandle = user.instagramHandle,
            address = user.address,
            announcementBanner = user.announcementBanner,
            totalVisits = user.totalVisits,
            phone = user.phone,
            subscriptionTier = user.subscriptionTier,
            subscriptionBillingCycle = user.subscriptionBillingCycle,
            subscriptionExpiresAt = user.subscriptionExpiresAt,
            isFounderPromo = user.isFounderPromo,
            monthlyAppointmentsCount = user.monthlyAppointmentsCount
        )

        return try {
            val provider = getAuthProvider()

            // 1. Reautenticação transparente antes da exclusão
            when (provider) {
                "google" -> {
                    val platformResult = br.com.heyagenda.domain.auth.PlatformAuthManager.signInWithGoogle()
                    val authData = platformResult.getOrThrow()
                    val idToken = authData.idToken ?: throw IllegalStateException("Token do Google não obtido.")
                    authDataSource.reauthenticateWithGoogle(idToken = idToken, accessToken = authData.accessToken)
                }
                "apple" -> {
                    val platformResult = br.com.heyagenda.domain.auth.PlatformAuthManager.signInWithApple()
                    val authData = platformResult.getOrThrow()
                    val idToken = authData.idToken ?: throw IllegalStateException("Token da Apple não obtido.")
                    authDataSource.reauthenticateWithApple(idToken = idToken, rawNonce = authData.rawNonce, accessToken = authData.accessToken)
                }
                else -> {
                    if (!password.isNullOrBlank()) {
                        authDataSource.reauthenticateWithPassword(password)
                    }
                }
            }

            val firestoreResult = firestoreDataSource.deleteProfile(user.uid)
            if (firestoreResult.isFailure) {
                val err = firestoreResult.exceptionOrNull() ?: Exception("Falha ao remover dados do perfil.")
                return Result.failure(Exception(br.com.heyagenda.domain.util.ErrorSanitizer.sanitize(err, "Falha ao remover dados do perfil. Tente novamente.")))
            }

            try {
                authDataSource.deleteCurrentUser()
            } catch (authError: Throwable) {
                try {
                    firestoreDataSource.saveProfile(user.uid, existingProfile)
                    println(">>> [DeleteAccount ROLLBACK] Profile restored in Firestore for ${user.uid}")
                } catch (rollbackErr: Throwable) {
                    println(">>> [DeleteAccount ROLLBACK ERROR] ${rollbackErr.message}")
                }
                throw authError
            }

            localDataSource.clearUser()
            _currentUser.value = null
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [DeleteAccount ERROR] ${e.message}")
            Result.failure(Exception(mapAuthException(e)))
        }
    }

    private suspend fun fetchUserProfile(firebaseUser: FirebaseUser): User {
        val uid = firebaseUser.uid
        val email = firebaseUser.email ?: ""
        var name = firebaseUser.displayName ?: ""
        var studioName: String? = null
        var specialty: String? = null
        var slug: String? = null
        var themeAccentColor: String? = null
        var instagramHandle: String? = null
        var address: String? = null
        var announcementBanner: String? = null
        var totalVisits: Long = 0L

        // Retain any existing local data as base fallback
        val existing = _currentUser.value ?: localDataSource.getUser()
        if (existing != null && existing.uid == uid) {
            if (name.isBlank()) name = existing.name
            studioName = existing.studioName
            specialty = existing.specialty
            slug = existing.slug
            themeAccentColor = existing.themeAccentColor
            instagramHandle = existing.instagramHandle
            address = existing.address
            announcementBanner = existing.announcementBanner
            totalVisits = existing.totalVisits
        }

        // Fetch latest Firestore profile
        val profile = firestoreDataSource.fetchProfile(uid)
        if (profile != null) {
            if (profile.name.isNotBlank()) name = profile.name
            if (!profile.studioName.isNullOrBlank()) studioName = profile.studioName
            if (!profile.specialty.isNullOrBlank()) specialty = profile.specialty
            if (!profile.slug.isNullOrBlank()) slug = profile.slug
            if (!profile.themeAccentColor.isNullOrBlank()) themeAccentColor = profile.themeAccentColor
            if (!profile.instagramHandle.isNullOrBlank()) instagramHandle = profile.instagramHandle
            if (!profile.address.isNullOrBlank()) address = profile.address
            if (!profile.announcementBanner.isNullOrBlank()) announcementBanner = profile.announcementBanner
            totalVisits = profile.totalVisits
        } else if (existing != null && existing.uid == uid && existing.name.isNotBlank() && !existing.studioName.isNullOrBlank()) {
            // Auto-heal Firestore only if account was previously fully configured
            try {
                firestoreDataSource.saveProfile(
                    uid = uid,
                    profile = ProfessionalProfileDto(
                        name = existing.name,
                        studioName = existing.studioName ?: "",
                        specialty = existing.specialty ?: "",
                        email = email,
                        slug = existing.slug,
                        themeAccentColor = existing.themeAccentColor,
                        instagramHandle = existing.instagramHandle,
                        address = existing.address,
                        announcementBanner = existing.announcementBanner,
                        totalVisits = existing.totalVisits
                    )
                )
            } catch (e: Throwable) {
                // Ignore auto-heal error
            }
        }

        // If user has no slug yet, generate one only if studioName is already set
        if (slug.isNullOrBlank()) {
            val base = (studioName ?: name).ifBlank { "agenda" }
            if (!studioName.isNullOrBlank()) {
                val uniqueSlug = firestoreDataSource.generateUniqueSlug(base, uid)
                slug = uniqueSlug
                try {
                    firestoreDataSource.updateSlug(uid, uniqueSlug)
                } catch (_: Throwable) {}
            } else {
                slug = br.com.heyagenda.domain.util.SlugUtils.toSlug(base)
            }
        }

        // Migrate legacy 'Grooming' category to 'Estética Masculina'
        if (specialty?.contains("Grooming", ignoreCase = true) == true) {
            val updatedSpecialty = specialty.replace("Grooming", "Estética Masculina", ignoreCase = true)
            specialty = updatedSpecialty
            try {
                val currentDto = profile ?: firestoreDataSource.fetchProfile(uid)
                if (currentDto != null && !currentDto.studioName.isNullOrBlank()) {
                    firestoreDataSource.saveProfile(uid, currentDto.copy(specialty = updatedSpecialty))
                }
            } catch (_: Throwable) {}
        }

        val user = User(
            uid = uid,
            name = name.ifBlank { "Profissional" },
            email = email,
            studioName = studioName?.ifBlank { null },
            specialty = specialty?.ifBlank { null },
            photoUrl = firebaseUser.photoURL,
            slug = slug,
            themeAccentColor = themeAccentColor,
            instagramHandle = instagramHandle,
            address = address,
            announcementBanner = announcementBanner,
            totalVisits = totalVisits,
            phone = profile?.phone,
            subscriptionTier = profile?.subscriptionTier ?: "free",
            subscriptionBillingCycle = profile?.subscriptionBillingCycle ?: "annual",
            subscriptionExpiresAt = profile?.subscriptionExpiresAt,
            isFounderPromo = profile?.isFounderPromo ?: true,
            monthlyAppointmentsCount = profile?.monthlyAppointmentsCount ?: 0
        )
        localDataSource.saveUser(user)
        return user
    }

    override suspend fun getIdToken(forceRefresh: Boolean): String? {
        return authDataSource.getIdToken(forceRefresh)
    }

    private fun mapAuthException(e: Throwable): String {
        return br.com.heyagenda.domain.util.ErrorSanitizer.sanitizeAuthError(e)
            ?: "Operação cancelada."
    }

    companion object {
        private val _currentUser = MutableStateFlow<User?>(null)
    }
}
